// MF-554 — Board editor (F4): Chart Library on the left, the 12-column bento grid in the middle
// (drag to move, drag the corner to resize, each item keeps its minimum size), properties on the
// right. Tiles show live previews with the school's data. Save keeps a draft; Publish checks the Board
// (compatible items, minimum sizes, student/parent-safe items) and creates its Analytics button.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { Alert, Box, Button, Chip, IconButton, Paper, Skeleton, Tooltip, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import GridLayout, { WidthProvider } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import useFetchData from '../../../../../hooks/useFetchData'
import {
  getBoard,
  getLibrary,
  previewLibraryItems,
  publishBoard,
  unpublishBoard,
  updateBoard
} from '../../../../../services/boards/boards.service'
import BoardTile from '../BoardTile'
import { BoardStyleContext } from '../boardPalettes'
import BuilderGuide from './BuilderGuide'
import { previewKey, sampleData } from '../sample'
import LibraryPanel from './LibraryPanel'
import { BoardProperties, TileProperties } from './PropertiesPanel'
import {
  COLS,
  applyGridLayout,
  bottomRow,
  fitMinimums,
  compactTiles,
  newTileId,
  sameLayout,
  sizeOf,
  toGridLayout
} from './layout'
import { GRID_GAP, ROW_HEIGHT, SUMMARY_PALETTE } from '../boardTheme'
import StudentSelect from '../StudentSelect'

const Grid = WidthProvider(GridLayout)

/**
 * The grid's cells drawn behind the tiles (12 columns × 30px rows, same geometry as react-grid-layout:
 * margin = container padding = GRID_GAP), so sizes like "3 columns × 6 rows" can be seen.
 */
const cellsBackground = (width) => {
  if (!width) return {}
  const colW = (width - GRID_GAP * (COLS + 1)) / COLS
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${colW + GRID_GAP}' height='${ROW_HEIGHT + GRID_GAP}'><rect width='${colW}' height='${ROW_HEIGHT}' rx='6' fill='rgba(58,110,165,0.07)'/></svg>`
  return {
    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`,
    backgroundSize: `${colW + GRID_GAP}px ${ROW_HEIGHT + GRID_GAP}px`,
    backgroundPosition: `${GRID_GAP}px ${GRID_GAP}px`
  }
}
// position is not here: the menu order is set in its own dialog (MenuOrderDialog), which updates the
// button and the Board together — a stale copy saved from here would move the button back.
const EDITABLE = ['name', 'description', 'tiles', 'audience', 'settings']

const BoardEditor = () => {
  const { boardId } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { data: roles } = useFetchData('roles')

  const [board, setBoard] = useState(null)
  const [saved, setSaved] = useState(null) // last saved copy → dirty check
  const [library, setLibrary] = useState(null)
  const [selected, setSelected] = useState(null)
  const [dragging, setDragging] = useState(null) // Library item being dragged onto the grid
  const [busy, setBusy] = useState(null)
  const [problems, setProblems] = useState(null)
  const [libraryOpen, setLibraryOpen] = useState(true)
  const [previewStudent, setPreviewStudent] = useState(null) // { code, label } — preview as this student
  const previewStudentId = previewStudent?.code || null
  const studentRef = useRef(null)
  const [previews, setPreviews] = useState({}) // "<code>|<params>" → { loading, data, error }
  const gridBoxRef = useRef(null)
  const [gridWidth, setGridWidth] = useState(0)
  useEffect(() => {
    const el = gridBoxRef.current
    if (!el) return undefined
    const ro = new ResizeObserver(([e]) => setGridWidth(e.contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [board === null])

  useEffect(() => {
    getBoard(boardId)
      .then((b) => {
        setBoard(b)
        setSaved(b)
      })
      .catch((err) => enqueueSnackbar(err.message || 'Board not found', { variant: 'error' }))
    getLibrary()
      .then((d) => setLibrary(d?.items || []))
      .catch(() => setLibrary([]))
  }, [boardId, enqueueSnackbar])

  // Esc goes back from a tile to the Board settings (unless the key is for a field or a dialog).
  useEffect(() => {
    if (!selected) return undefined
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if (e.target.closest?.('input, textarea, select, [role="dialog"], [role="listbox"]')) return
      setSelected(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selected])

  const itemsByCode = useMemo(() => new Map((library || []).map((i) => [i.code, i])), [library])
  // Once both are loaded: tiles smaller than their item's minimum grow to it (the Board shows as
  // unsaved, so saving fixes the published view too).
  const loaded = Boolean(board && library)
  useEffect(() => {
    if (!loaded) return
    setBoard((b) => {
      const fixed = fitMinimums(b.tiles || [], itemsByCode)
      return fixed ? { ...b, tiles: fixed } : b
    })
  }, [loaded, boardId, itemsByCode])
  const tiles = board?.tiles || []

  // Live previews, fetched in ONE request for whatever tiles still miss one. Until it arrives a tile
  // shows its item's sample rows (labelled "Sample data"), so adding a tile is instant.
  const missingKey = tiles
    .map((t) => previewKey(t.itemCode, t.overrides?.params))
    .filter((k, i, a) => a.indexOf(k) === i && !previews[k])
    .join('\n') // params JSON may contain commas
  // Another student (or the whole school) = other numbers: forget the previews and ask again.
  useEffect(() => {
    studentRef.current = previewStudentId
    setPreviews({})
  }, [previewStudentId])

  useEffect(() => {
    if (!missingKey) return
    const asStudent = previewStudentId
    const stale = () => studentRef.current !== asStudent // an answer for a student no longer picked
    const keys = missingKey.split('\n')
    setPreviews((p) => ({ ...p, ...Object.fromEntries(keys.map((k) => [k, { loading: true }])) }))
    const items = keys.map((k) => {
      const i = k.indexOf('|')
      return { code: k.slice(0, i), params: JSON.parse(k.slice(i + 1)) }
    })
    previewLibraryItems(items, asStudent)
      .then((d) =>
        stale()
          ? null
          : setPreviews((p) => ({
              ...p,
              ...Object.fromEntries(
                keys.map((k) => [
                  k,
                  {
                    loading: false,
                    data: d?.[k]?.data || null,
                    error:
                      (typeof d?.[k]?.error === 'string' ? d[k].error : d?.[k]?.error?.message) ||
                      (d?.[k] ? null : 'No preview')
                  }
                ])
              )
            }))
      )
      .catch((err) =>
        stale()
          ? null
          : setPreviews((p) => ({
              ...p,
              ...Object.fromEntries(
                keys.map((k) => [k, { loading: false, data: null, error: err?.message || 'No preview' }])
              )
            }))
      )
  }, [missingKey, previewStudentId])

  const tileResult = (tile) => {
    const r = previews[previewKey(tile.itemCode, tile.overrides?.params)]
    if (r && !r.loading) return r
    // Sample rows only while no student is picked (adding a tile is instant). With a student chosen the tile waits for
    // THEIR data: showing made-up numbers first and then replacing them looks like data that disappears.
    const sample = previewStudentId ? null : sampleData(itemsByCode.get(tile.itemCode))
    return sample ? { loading: false, data: sample, error: null } : { loading: true, data: null, error: null }
  }
  const dirty = useMemo(
    () =>
      Boolean(board && saved) &&
      EDITABLE.some((k) => JSON.stringify(board[k] ?? null) !== JSON.stringify(saved[k] ?? null)),
    [board, saved]
  )
  const patch = (p) => setBoard((b) => ({ ...b, ...p }))

  const addItem = useCallback(
    (item, at = null) => {
      const { w, h } = sizeOf(item)
      const tile = {
        id: newTileId(item.code),
        itemCode: item.code,
        x: at?.x ?? 0,
        y: at?.y ?? bottomRow(tiles),
        w,
        h,
        overrides: {}
      }
      patch({ tiles: compactTiles([...tiles, tile]) })
      setSelected(tile.id)
    },
    [tiles]
  )

  const onLayoutChange = (layout) => {
    // Ignore react-grid-layout's temporary drop placeholder; never keep overlapping tiles.
    const next = compactTiles(
      applyGridLayout(
        tiles,
        layout.filter((l) => l.i !== '__dropping__')
      )
    )
    if (!sameLayout(next, tiles)) patch({ tiles: next })
  }

  const save = async (silent = false) => {
    setBusy('save')
    setProblems(null)
    try {
      const body = Object.fromEntries(EDITABLE.map((k) => [k, board[k]]))
      body.tiles = compactTiles(body.tiles || [])
      const b = await updateBoard(boardId, body)
      // keep what the editor already has (id included) and take what the server answered
      setBoard((cur) => ({ ...cur, ...b, _id: boardId }))
      setSaved(b)
      if (!silent)
        enqueueSnackbar(b.status === 'published' ? 'Saved — the published board is updated' : 'Draft saved', {
          variant: 'success'
        })
      return b
    } catch (err) {
      setProblems(err.message)
      return null
    } finally {
      setBusy(null)
    }
  }

  const publish = async () => {
    if (dirty && !(await save(true))) return
    setBusy('publish')
    setProblems(null)
    try {
      const { publishNotice, ...b } = await publishBoard(boardId)
      setBoard((cur) => ({ ...cur, ...b, _id: boardId }))
      setSaved(b)
      enqueueSnackbar('Published — users see it in Analytics after their next login', { variant: 'success' })
      // MF-575 — a tile without data for this school does not block: it stays hidden until the data arrives
      if (publishNotice?.noData?.length)
        enqueueSnackbar(
          `${publishNotice.noData.length} tile${publishNotice.noData.length === 1 ? ' has' : 's have'} no data in this school and stay hidden until it arrives: ${publishNotice.noData.map((n) => n.split(':')[0]).join(', ')}`,
          { variant: 'info', autoHideDuration: 10000 }
        )
    } catch (err) {
      setProblems(err.message)
    } finally {
      setBusy(null)
    }
  }

  const unpublish = async () => {
    setBusy('publish')
    try {
      const b = await unpublishBoard(boardId)
      setBoard((cur) => ({ ...cur, ...b, _id: boardId }))
      setSaved(b)
      enqueueSnackbar('Unpublished — its Analytics button was removed', { variant: 'success' })
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' })
    } finally {
      setBusy(null)
    }
  }

  if (!board) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton width={300} height={40} />
        <Skeleton variant='rounded' height={400} sx={{ mt: 2 }} />
      </Box>
    )
  }

  const selectedTile = tiles.find((t) => t.id === selected)
  const published = board.status === 'published'

  return (
    <BoardStyleContext.Provider value={board.settings || {}}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
        {/* top bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${SUMMARY_PALETTE.cardBorder}`,
            flexWrap: 'wrap'
          }}
        >
          <IconButton size='small' onClick={() => navigate('..')} aria-label='Back to boards'>
            <FontAwesomeIcon icon={['fal', 'arrow-left']} />
          </IconButton>
          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 18, fontWeight: 700, color: SUMMARY_PALETTE.ink }}>
              {board.name || 'Untitled board'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted }}>
              {tiles.length} tile{tiles.length === 1 ? '' : 's'} · drag to move, drag the corner to resize
            </Typography>
          </Box>
          <Chip
            size='small'
            label={published ? 'Published' : 'Draft'}
            color={published ? 'success' : 'default'}
            variant={published ? 'filled' : 'outlined'}
          />
          <BuilderGuide />
          <StudentSelect
            value={previewStudent}
            onChange={setPreviewStudent}
            label='Preview as student'
            sx={{ minWidth: 240, ml: 'auto' }}
          />
          {dirty ? <Chip size='small' color='warning' variant='outlined' label='Unsaved changes' /> : null}
          <Button variant='outlined' disabled={!dirty || Boolean(busy)} onClick={() => save()}>
            Save
          </Button>
          {published ? (
            <>
              <Tooltip title='Open it as users see it (new tab)'>
                <span>
                  <Button
                    disabled={dirty}
                    onClick={() =>
                      window.open(`/dashboard/analytics/board-${board.code}`, '_blank', 'noopener')
                    }
                  >
                    View
                  </Button>
                </span>
              </Tooltip>
              <Button color='inherit' disabled={Boolean(busy)} onClick={unpublish}>
                Unpublish
              </Button>
            </>
          ) : (
            <Button variant='contained' disabled={Boolean(busy) || tiles.length === 0} onClick={publish}>
              Publish
            </Button>
          )}
        </Box>

        {problems ? (
          <Alert severity='warning' onClose={() => setProblems(null)} sx={{ mx: 3, mt: 2 }}>
            {problems}
          </Alert>
        ) : null}

        <Box sx={{ display: 'flex', gap: 2, p: 2, flex: 1, minHeight: 0, minWidth: 0 }}>
          {/* library */}
          {libraryOpen ? (
            <Paper
              variant='outlined'
              sx={{
                width: 240,
                flexShrink: 0,
                p: 1.5,
                borderRadius: '14px',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
              }}
            >
              <LibraryPanel
                items={library}
                onAdd={(item) => addItem(item)}
                onDragItem={setDragging}
                onHide={() => setLibraryOpen(false)}
              />
            </Paper>
          ) : (
            <Tooltip title='Show the Chart Library'>
              <Button
                variant='outlined'
                onClick={() => setLibraryOpen(true)}
                sx={{ minWidth: 0, px: 1.25, alignSelf: 'flex-start' }}
              >
                <FontAwesomeIcon icon={['fal', 'books']} />
              </Button>
            </Tooltip>
          )}

          {/* grid */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflowY: 'auto',
              borderRadius: '14px',
              bgcolor: 'rgba(15,23,42,0.025)',
              p: 1
            }}
            onClick={(e) => e.target === e.currentTarget && setSelected(null)}
          >
            {tiles.length === 0 ? (
              <Typography
                sx={{ color: SUMMARY_PALETTE.muted, textAlign: 'center', pt: 6, pointerEvents: 'none' }}
              >
                Add charts and cards from the Chart Library (click + or drag them here).
              </Typography>
            ) : null}
            <Box
              ref={gridBoxRef}
              sx={{ ...cellsBackground(gridWidth), borderRadius: '10px' }}
              onClick={(e) => !e.target.closest('.react-grid-item') && setSelected(null)}
            >
              <Grid
                className='board-builder-grid'
                layout={toGridLayout(tiles, itemsByCode)}
                cols={COLS}
                rowHeight={ROW_HEIGHT}
                margin={[GRID_GAP, GRID_GAP]}
                compactType='vertical'
                draggableCancel='.no-drag'
                resizeHandles={['se']}
                isDroppable
                droppingItem={dragging ? { i: '__dropping__', ...sizeOf(dragging) } : undefined}
                onDrop={(layout, item, e) => {
                  const code = e.dataTransfer?.getData('text/plain')
                  const libItem = itemsByCode.get(code)
                  if (libItem?.compatible) addItem(libItem, { x: item.x, y: item.y })
                  setDragging(null)
                }}
                onLayoutChange={onLayoutChange}
                // a few empty rows of cells under the last tile, to drop or grow into
                style={{
                  minHeight: Math.max(360, (bottomRow(tiles) + 8) * (ROW_HEIGHT + GRID_GAP) + GRID_GAP)
                }}
              >
                {tiles.map((tile) => (
                  <div key={tile.id}>
                    {/* select on press, on an inner box: react-grid-layout replaces the grid item's own mouse
                      handlers, and starting a drag swaps the element under the pointer, so no click
                      ever fires */}
                    <Box sx={{ height: '100%' }} onMouseDown={() => setSelected(tile.id)}>
                      <BoardTile
                        preview
                        result={tileResult(tile)}
                        tile={tile}
                        selected={tile.id === selected}
                        actions={
                          <Tooltip title='Remove'>
                            <IconButton
                              size='small'
                              className='no-drag'
                              onClick={(e) => {
                                e.stopPropagation()
                                patch({ tiles: tiles.filter((t) => t.id !== tile.id) })
                                if (selected === tile.id) setSelected(null)
                              }}
                            >
                              <FontAwesomeIcon icon={['fal', 'times']} style={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                        }
                      />
                    </Box>
                  </div>
                ))}
              </Grid>
            </Box>
          </Box>

          {/* properties */}
          <Paper
            variant='outlined'
            sx={{ width: 280, flexShrink: 0, p: 2, borderRadius: '14px', overflowY: 'auto' }}
          >
            {selectedTile ? (
              <TileProperties
                tile={selectedTile}
                item={itemsByCode.get(selectedTile.itemCode)}
                onChange={(t) => patch({ tiles: tiles.map((x) => (x.id === t.id ? t : x)) })}
                onRemove={() => {
                  patch({ tiles: tiles.filter((t) => t.id !== selectedTile.id) })
                  setSelected(null)
                }}
                onClose={() => setSelected(null)}
              />
            ) : (
              <BoardProperties board={board} roles={roles} onChange={patch} />
            )}
          </Paper>
        </Box>
      </Box>
    </BoardStyleContext.Provider>
  )
}

export default BoardEditor
